import type { SalesCoverage } from "~/features/freeCapacityPush/useFCCState";

import { Bar } from "react-chartjs-2";
import {
  C_CHART_1,
  C_CHART_2,
  C_CHART_3,
  C_CHART_4,
  C_CHART_5,
  C_ON_SURFACE,
  C_SURFACE,
} from "~/common/components/icon/Colors";
import { Trans, useTranslation } from "react-i18next";
import { centeredTextPlugin, formatYTicks, useRerenderOnResize } from "~/features/freeCapacityPush/graphUtils";
import { isNonNull } from "~/common/utils/reducers";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { InfoCircle } from "~/common/components/InfoCircle/InfoCircle";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import type { CoverageType, CoverageUnit } from "~/features/freeCapacityPush/api/fetchTrainDetails";
import { MenuItem, styled } from "~/common/mui";
import { Chart as ChartJS } from "chart.js/auto";

import {
  CategoryScale,
  type ChartData,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { TextField } from "~/common/mui/TextField/TextField";
import {
  FCCTileCard,
  FCCTileContent,
  FCCTileSubtitle,
  FCCTileTitle,
} from "~/features/freeCapacityPush/components/FCCTileStyles";
import { useCommerceContext } from "~/app/config/useSession";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const InfoIcon = styled(InfoCircle)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(3),
  top: theme.spacing(3),
}));

const TooltipContent = styled("div")(({ theme }) => ({
  width: 282,
  whiteSpace: "pre-line",
  padding: theme.spacing(1),
}));

const Content = styled(FCCTileContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  [theme.breakpoints.up("desktop-s")]: {
    gap: theme.spacing(3),
  },
}));

const Selects = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "2fr 1fr",
  gap: theme.spacing(1),
}));

interface Props {
  className?: string;
  salesCoverage?: SalesCoverage;
  selectedProductCode?: string;
  productLineLabel?: string;
  salesCoverageType: CoverageType;
  salesCoverageUnit: CoverageUnit;
  onSalesCoverageTypeChange: (coverageType: CoverageType) => void;
  onSalesCoverageUnitChange: (unit: CoverageUnit) => void;
}

const BG_COLORS = [C_CHART_5, C_CHART_4, C_CHART_3, C_CHART_2, C_CHART_1];

export const SalesCoverageTile = ({
  className,
  salesCoverage,
  selectedProductCode,
  productLineLabel,
  salesCoverageType,
  salesCoverageUnit,
  onSalesCoverageTypeChange,
  onSalesCoverageUnitChange,
}: Props) => {
  const { t } = useTranslation();
  const account = useCommerceContext()?.selectedAccount;
  const isDesktop = useIsMinDesktopSize();

  const salesCoverageValues = salesCoverage?.[salesCoverageType]?.[salesCoverageUnit];

  const productDescriptor = ProductService.useNullableProductDescriptor({
    productCode: selectedProductCode ?? "",
    priceReference: "",
  });

  const data: ChartData<"bar"> = {
    labels: undefined,
    datasets: [
      {
        data: [],
        backgroundColor: BG_COLORS,
        borderRadius: 4,
        borderSkipped: false,
        barThickness: 32,
      },
    ],
  };
  let key = "-";
  if (salesCoverageValues) {
    const { ytdSales = 0, orderbook = 0, gap = 0, plan = 0, budget = 0 } = salesCoverageValues;

    data.labels = [
      t("page.fcc.salesCoverage.ytd"),
      t("page.fcc.salesCoverage.ob"),
      t("page.fcc.salesCoverage.gap"),
      t("page.fcc.salesCoverage.plan"),
    ];
    data.datasets[0].data = [
      [0, ytdSales],
      [ytdSales, ytdSales + orderbook],
      [ytdSales + orderbook, ytdSales + orderbook + gap],
      [0, plan],
    ];
    if (!selectedProductCode && salesCoverageType !== "FCC_CATALOG_ONLY") {
      data.labels.push(t("page.fcc.salesCoverage.budget"));
      data.datasets[0].data.push([0, budget]);
    }
    key = [ytdSales, orderbook, gap, plan, budget].join("-");
  }

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: true,
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            const { start = null, end = null } = context.parsed._custom ?? {};
            if (start !== null && end !== null) {
              const val = end - start;
              return NF.getDefaultFormatter().format(val);
            }
            return "";
          },
        },
        // @ts-ignore - this exists
        labelTextColor: C_ON_SURFACE,
        titleColor: C_ON_SURFACE,
        bodyColor: C_ON_SURFACE,
        backgroundColor: C_SURFACE,
      },
    },
    scales: {
      y: {
        ticks: {
          display: !!salesCoverageValues,
          callback: formatYTicks,
        },
        border: {
          display: !!salesCoverageValues,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const accountNumber = account?.displayUid ?? "N/A";

  let subtitle;
  if (selectedProductCode) {
    subtitle = productDescriptor?.base.designation;
  } else if (productLineLabel) {
    subtitle = t("page.fcc.salesCoverage.productLine", { name: productLineLabel });
  } else {
    subtitle = t("page.fcc.account") + " " + accountNumber;
  }

  const tooltipContent = (
    <TooltipContent>
      <Trans i18nKey={"page.fcc.salesCoverage.tooltip"} />
    </TooltipContent>
  );

  const height = isDesktop ? 186 : 140;
  const [containerRef, chartRef] = useRerenderOnResize();

  return (
    <FCCTileCard className={className}>
      <InfoIcon content={tooltipContent} placement={"left"} />
      <FCCTileTitle>
        <span>{t("page.fcc.salesCoverage.title")}</span>
      </FCCTileTitle>
      <FCCTileSubtitle title={productLineLabel}>{subtitle}</FCCTileSubtitle>
      <Content>
        <Selects>
          <TextField
            select
            size={"small"}
            variant={"outlined"}
            value={salesCoverageType}
            onChange={(e) => onSalesCoverageTypeChange(e.target.value as CoverageType)}
          >
            <MenuItem value={"CUSTOMER_CATALOG"}>{t("page.fcc.salesCoverage.option.customerCatalog")}</MenuItem>
            <MenuItem value={"FCC_CATALOG_ONLY"}>{t("page.fcc.salesCoverage.option.fccOnly")}</MenuItem>
          </TextField>
          <TextField
            select
            size={"small"}
            variant={"outlined"}
            value={salesCoverageUnit}
            onChange={(e) => onSalesCoverageUnitChange(e.target.value as CoverageUnit)}
          >
            <MenuItem value={"EURO"}>{t("page.fcc.salesCoverage.option.euro")}</MenuItem>
            <MenuItem value={"PIECES"}>{t("page.fcc.salesCoverage.option.pieces")}</MenuItem>
          </TextField>
        </Selects>
        <div className={"w-100"} style={{ height: height + "px" }} ref={containerRef}>
          <Bar
            key={key}
            // @ts-ignore
            ref={chartRef}
            options={options}
            data={data}
            height={height}
            plugins={[
              !salesCoverageValues &&
                centeredTextPlugin(
                  t("page.fcc.salesCoverage.noData"),
                  getFontStyle(FontSize.TitleLarge).fontSize,
                  C_ON_SURFACE,
                ),
            ].filter(isNonNull)}
          />
        </div>
      </Content>
    </FCCTileCard>
  );
};
